import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Typography,
  Box,
  Checkbox,
} from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import { useToast } from "../../services/utils";
import useFetchUsers from "../../hooks/useFetchUsers";
import { assignUserToGroup } from "../../services/groupService";
import Loader from "../Molecules/Loader/Loader";

interface User {
  id: number;
  name?: string;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

interface AddUserToGroupModalProps {
  allUsers: User[];
  open: boolean;
  onClose: () => void;
  groupId: string | number;
  groupName: string;
  onSuccess?: () => void;
  assignedUserIds: number[];
}

const AddUserToGroupModal: React.FC<AddUserToGroupModalProps> = ({
  allUsers,
  open,
  onClose,
  groupId,
  groupName,
  onSuccess,
  assignedUserIds,
}) => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedUserIds([]);
    }
  }, [open]);

  // Helper function to get user display name
  const getUserDisplayName = (user: User) => {
    if (user?.name) return user.name;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return "Unknown User";
  };

  // Helper function to get user initials for avatar
  const getUserInitials = (user: User) => {
    const displayName = getUserDisplayName(user);
    const names = displayName.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  // Handle user selection
  const handleUserSelect = (userId: number) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectedUserIds.length === availableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(availableUsers.map((user) => user.id));
    }
  };

  // Filter users based on search term and exclude already assigned users
  const availableUsers =
    allUsers?.filter((user: User) => {
      // Exclude already assigned users
      if (assignedUserIds.includes(user.id)) return false;

      // Apply search filter
      if (!searchTerm.trim()) return true;

      const displayName = getUserDisplayName(user).toLowerCase();
      const email = user.email?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      return displayName.includes(searchLower) || email.includes(searchLower);
    }) || [];

  // Handle save changes
  const handleSave = async () => {
    if (selectedUserIds.length === 0) {
      showToast("Please select at least one user to add", "warning");
      return;
    }

    try {
      setIsLoading(true);
      const payload: any = {
        group_id: Number(groupId),
        user_ids: [...assignedUserIds, ...selectedUserIds],
      };

      await assignUserToGroup(payload);
      showToast(
        `${selectedUserIds.length} user(s) added to group successfully`,
        "success",
      );
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error adding users to group:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAllSelected =
    selectedUserIds.length === availableUsers.length &&
    availableUsers.length > 0;
  const isIndeterminate =
    selectedUserIds.length > 0 &&
    selectedUserIds.length < availableUsers.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="modal-dialog-1"
    >
      <Loader isLoading={isLoading} />
      <DialogTitle className="dialog-title">
        <span>Add people to '{groupName}'</span>
        <button className="close-icon" onClick={onClose}></button>
      </DialogTitle>

      <DialogContent className="dialog-content">
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search for People"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Select All Option */}
        {availableUsers.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <ListItem
              // button
              onClick={handleSelectAll}
              sx={{ cursor: "pointer" }}
            >
              <ListItemAvatar>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  sx={{
                    "&.Mui-checked": {
                      color: "#0E1223",
                    },
                    "&.MuiCheckbox-indeterminate": {
                      color: "#0E1223",
                    },
                  }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={`Select All (${availableUsers.length} users)`}
              />
            </ListItem>
          </Box>
        )}

        {/* Available Users List */}
        {availableUsers.length > 0 ? (
          <List dense>
            {availableUsers.map((user: User) => (
              <ListItem
                key={user.id}
                button
                onClick={() => handleUserSelect(user.id)}
                sx={{ cursor: "pointer" }}
              >
                <ListItemAvatar className="main-header no-bg">
                  <Avatar className="username">{getUserInitials(user)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={getUserDisplayName(user)}
                  secondary={user.email}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    checked={selectedUserIds.includes(user.id)}
                    onChange={() => handleUserSelect(user.id)}
                    sx={{
                      "&.Mui-checked": {
                        color: "#0E1223",
                      },
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              {searchTerm.trim()
                ? "No users found matching your search."
                : "All users are already assigned to this group."}
            </Typography>
          </Box>
        )}

        {selectedUserIds.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedUserIds.length} user(s) selected
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="dialog-footer">
        <Button
          onClick={onClose}
          disabled={isLoading}
          className="btn-orange btn-dark"
          sx={{ marginRight: "8px" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="btn-orange"
          disabled={selectedUserIds.length === 0 || isLoading}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserToGroupModal;
